import { LogOut } from "lucide-react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { useLogout, useUsername } from "@/auth/AuthProvider";
import { useIsAdmin, useIsLauncherOnly, useSurveysOnTheWeb } from "@/auth/useMe";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface NavItem {
  label: string;
  // When set, the item is a live route; otherwise it renders as a disabled
  // "coming soon" stub communicating the planned IA.
  to?: string;
}

// What everyone sees: the facts they can look up, and the survey we want them
// to run (when the deployment offers it on the web: ``SURVEYS_PATH`` is dropped
// otherwise). The NavItem stub shape stays for future additions (e.g. the M3
// admin chat).
const SURVEYS_PATH = "/preferences";
const NAV_ITEMS: NavItem[] = [
  { label: "Current Systems", to: "/systems" },
  { label: "Surveys", to: SURVEYS_PATH },
  { label: "Launches", to: "/launches" },
];

// Rendered only for admins, in nav order after the open surfaces. Opinions and
// Skills are here because the aggregate isn't ready to be the team-visible
// layer yet — the interviews have to run first. Hiding them is UX, not
// security: ``AdminGate`` guards each route and the admin APIs 403 on their own.
// The Leadership Team (``launcher_only``) gets this and nothing else; the route
// guard and the API enforce the same line.
const LAUNCHER_ONLY_NAV_ITEMS: NavItem[] = [{ label: "Launches", to: "/launches" }];

const ADMIN_NAV_ITEMS: NavItem[] = [
  { label: "Opinions", to: "/opinions" },
  { label: "Skills", to: "/skills" },
  { label: "Admin", to: "/admin" },
];

/**
 * Top-level chrome for Northstar. Logo follows the brand's wordmark spec
 * (Hanken Grotesk 700, navy) since we don't have a raster logo committed
 * to the repo. A NavItem without ``to`` renders as a disabled placeholder
 * until its milestone lands.
 */
export function Layout() {
  const username = useUsername();
  const logout = useLogout();
  const isAdmin = useIsAdmin();
  const launcherOnly = useIsLauncherOnly();
  const surveysOn = useSurveysOnTheWeb();
  const openItems = surveysOn ? NAV_ITEMS : NAV_ITEMS.filter((item) => item.to !== SURVEYS_PATH);
  const navItems = launcherOnly
    ? LAUNCHER_ONLY_NAV_ITEMS
    : isAdmin
      ? [...openItems, ...ADMIN_NAV_ITEMS]
      : openItems;

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <header className="border-b border-bw-blue-muted/40">
        <div className="mx-auto max-w-7xl px-6 py-4 flex items-center gap-8">
          <Link
            to={launcherOnly ? "/launches" : "/"}
            className="flex items-baseline gap-2 text-bw-navy"
            aria-label="North Star home"
          >
            <span className="font-sans text-lg font-bold tracking-tight">
              North Star
            </span>
            <span className="font-sans text-sm font-normal text-bw-ink-muted">
              BluWave
            </span>
          </Link>
          {/* Live items are NavLinks (navy + blue underline when active);
              the rest stay disabled "coming soon" stubs until their phase. */}
          <nav className="flex gap-6 font-sans text-sm">
            {navItems.map((item) =>
              item.to ? (
                <NavLink
                  key={item.label}
                  to={item.to}
                  className={({ isActive }) =>
                    cn(
                      "py-1 border-b-2 transition-colors",
                      isActive
                        ? "border-bw-blue text-bw-navy font-semibold"
                        : "border-transparent text-bw-ink-muted hover:text-bw-navy",
                    )
                  }
                >
                  {item.label}
                </NavLink>
              ) : (
                <span
                  key={item.label}
                  aria-disabled="true"
                  title="Coming soon"
                  className={cn(
                    "py-1 border-b-2 border-transparent cursor-not-allowed",
                    "text-bw-ink-muted/60 select-none",
                  )}
                >
                  {item.label}
                  <span className="ml-1 align-super text-[10px] font-normal text-bw-ink-muted/60">
                    soon
                  </span>
                </span>
              ),
            )}
          </nav>
          <div className="ml-auto flex items-center gap-4">
            {username && (
              <span className="font-sans text-sm text-bw-ink-muted">{username}</span>
            )}
            <Button variant="ghost" size="sm" onClick={logout}>
              <LogOut className="h-4 w-4" />
              Sign out
            </Button>
          </div>
        </div>
      </header>
      <main className="flex-1 mx-auto max-w-7xl w-full px-6 py-8">
        <Outlet />
      </main>
    </div>
  );
}
